import React, {useContext, useEffect, useRef, useState} from "react";
import {Box, Grid} from "@mui/material";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {owAncillaryExceptionFiltersAtom,} from "../../state/owAncillaryExceptionRecoil";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE,} from "../../api/types/generic";
import dayjs from "dayjs";
import {
    FormSearchOWAncillaryExceptionModel,
    OWAncillaryExceptionEntityResponse,
} from "../../api/types/owAncillaryException";
import {AgGridReact} from "ag-grid-react";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../api/types/commercialCategory";
import {commercialCategoryListAtom} from "../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../state/commercialSegmentsAtom";
import {
    dateFormatter,
    fromGenericBeToGenericFe,
    fromOWAncillaryBeToFe,
    i18nConvertNumberWithDecimals,
    useActionColDef,
} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {useCommercialCategoryRepository} from "../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../api/repositories/CommercialSegmentRepository";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {useOWAncillaryExceptionRepository} from "../../api/repositories/OWAncillaryExceptionRepository";
import BasicToolbar from "../../common/components/BasicToolbar";
import {ancillaryListAtom} from "../../state/ancillariesRecoil";
import ActionsPopover from "../../common/components/ActionsPopover";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import DialogComponent from "../../common/components/DialogComponent";
import {OWAncillaryEntityResponse} from "../../api/types/owAncillary";
import Loading from "../../common/components/Loading";
import OWAncillaryExceptionContainerView from "../OWAncillaryException/OWAncillaryExceptionContainerView";
import {useOWAncillaryRepository} from "../../api/repositories/OWAncillaryRepository";
import OWAncillaryExceptionFilters from "./Filters/OWAncillaryExceptionFilters";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import {AncillarySelectFE} from "../../api/types/ancillary";

const OWAncillaryExceptions = () => {
    // Use Translation
    const {t, i18n} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [owAncillaryExceptionFilters, setOWAncillaryExceptionFilters] =
        useRecoilState(owAncillaryExceptionFiltersAtom);
    const [, setCommercialCategoryList] = useRecoilState(
        commercialCategoryListAtom
    );
    const [, setCommercialSegmentList] = useRecoilState(
        commercialSegmentListAtom
    );
    const [, setOWAncillaryList] = useRecoilState(ancillaryListAtom);

    // Use Repository Hooks
    const {
        getOWAncillaryExceptionsPaged,
        deleteOWAncillaryException,
        terminateOWAncillaryException
    } = useOWAncillaryExceptionRepository();
    const {getOWAncillaries} = useOWAncillaryRepository();
    const {getCommercialCategories} = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // Use State
    const [viewOWAncillaryException, setViewOWAncillaryException] =
        useState<boolean>(false);
    const [rowIndex, setRowIndex] = useState<number | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = 
        useState<CommercialCategorySelectFE[]>([]);

    const handleGetStaticContent = async () => {
        const commercialCategoriesData: CommercialCategoryEntityResponse[] = await getCommercialCategories();
        const commercialCategoryList: CommercialCategorySelectFE[] =
            fromGenericBeToGenericFe(commercialCategoriesData);
        setCommercialCategoryList(commercialCategoryList);
        setCommercialCategoryListOriginal(commercialCategoryList);

        const commercialSegmentsData = await getCommercialSegments();
        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);

        const owAncillariesData: OWAncillaryEntityResponse[] = await getOWAncillaries();
        const owAncillaryList: AncillarySelectFE[] =
            fromOWAncillaryBeToFe(owAncillariesData);
        setOWAncillaryList(owAncillaryList);
    };

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchOWAncillaryExceptionModel>({
        commercialCategoryId: null,
        commercialSegmentId: null,
        name: "",
        productCode: "",
        ancillaryId: null,
        reservationFrom: null,
        reservationTo: null,
        pickupDateFrom: null,
        pickupDateTo: null,
        dealers: dealerOption,
        legalEntities: legalEntityOptions
    })

    /* AG GRID - START */
    // Use Grid Data Custom Hook - entityFilters: inizializzato con stringhe per API
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<
            OWAncillaryExceptionEntityResponse,
            FormSearchOWAncillaryExceptionModel
        >({
            gridRef,
            api: getOWAncillaryExceptionsPaged,
            initialSortColId: "name",
            entityFilters: {
                commercialCategoryId: null,
                commercialSegmentId: null,
                name: "",
                productCode: "",
                ancillaryId: null,
                reservationFrom: null,
                reservationTo: null,
                pickupDateFrom: null,
                pickupDateTo: null,
                dealers: dealerFilteredOptions,
                legalEntities: everyLegalEntitiesWithoutAll
            },
            customSort: "OWAncillaryExceptionEntityResponse",
        });

    // Effettua la prima ricerca al mount
    useEffect(() => {
        handleTriggerResetFilters();
    }, []);

    const handleTriggerFiltersInitial = () => {
        const legalEntitiesArray = Array.isArray(testFilters.legalEntities)
            ? (testFilters.legalEntities as SelectElementModelFE[]).map((le: SelectElementModelFE) => le.code as string)
            : [];
        
        const dealersArray = Array.isArray(testFilters.dealers)
            ? (testFilters.dealers as SelectElementModelFE[]).map((d: SelectElementModelFE) => d.code as string)
            : [];

        handleTriggerFilters({
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            name: testFilters.name,
            reservationFrom: testFilters.reservationFrom
                ? dayjs(testFilters.reservationFrom).toISOString()
                : testFilters.reservationFrom,
            reservationTo: testFilters.reservationTo
                ? dayjs(testFilters.reservationTo).toISOString()
                : testFilters.reservationTo,
            pickupDateFrom: testFilters.pickupDateFrom
                ? dayjs(testFilters.pickupDateFrom).toISOString()
                : testFilters.pickupDateFrom,
            pickupDateTo: testFilters.pickupDateTo
                ? dayjs(testFilters.pickupDateTo).toISOString()
                : testFilters.pickupDateTo,
            productCode: testFilters.productCode,
            ancillaryId: (
                testFilters.ancillaryId as SelectElementModelFE
            )?.code as number,
            legalEntities: legalEntitiesArray,
            dealers: dealersArray,
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleTriggerResetFilters = () => {
        setTestFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            name: "",
            productCode: "",
            ancillaryId: null,
            reservationFrom: null,
            reservationTo: null,
            pickupDateFrom: null,
            pickupDateTo: null,
            dealers: dealerOption,
            legalEntities: legalEntityOptions
        });
        handleTriggerFilters({
            commercialCategoryId: null,
            commercialSegmentId: null,
            name: "",
            productCode: "",
            ancillaryId: null,
            reservationFrom: null,
            reservationTo: null,
            pickupDateFrom: null,
            pickupDateTo: null,
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
    };


    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "legalEntities",
            headerName: t("pricing:ancillaryException.table.labels.legalEntities"),
            headerTooltip: t("pricing:ancillaryException.table.labels.legalEntities"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.prcAncillary.legalEntity)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:ancillaryException.table.labels.dealers"),
            headerTooltip: t("pricing:ancillaryException.table.labels.dealers"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.prcAncillary.dealer)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "name",
            headerName: `${t("pricing:ancillaryException.table.labels.name")}`,
            headerTooltip: `${t("pricing:ancillaryException.table.labels.name")}`,
            flex: 1,
            filter: "text"
        },
        {
            field: "commercialSegment.name",
            colId: "prcCommercialSegment",
            valueFormatter: (params) => params.value,
            headerName: t("pricing:ancillaryException.form.labels.commercialSegment"),
            headerTooltip: t("pricing:ancillaryException.form.labels.commercialSegment"),
            flex: 1,
            filter: "text"
        },
        {
            field: "commercialCategory",
            colId: "commercialCategoryId",
            valueFormatter: (params) => {
                const category = params.value;
                const legalEntity = params.data?.legalEntity;
                
                if (!category) {
                    return '';
                }
                
                if (!category.labels || category.labels.length === 0) {
                    return category.name || '';
                }
                
                const labelObj = category.labels.find((l: any) => l.country === legalEntity);
                return labelObj?.label || category.name || '';
            },
            headerName: t("pricing:ancillaryException.table.labels.commercialCategory"),
            headerTooltip: t("pricing:ancillaryException.table.labels.commercialCategory"),
            flex: 1,
            filter: "text"
        },
        {
            field: "prcAncillary.nameAncillary",
            colId: "ancillaryId",
            valueFormatter: (params) => params.value,
            headerName: `${t("pricing:ancillaryException.table.labels.ancillary")}`,
            headerTooltip: `${t("pricing:ancillaryException.table.labels.ancillary")}`,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams<OWAncillaryExceptionEntityResponse>) => {
                // LPP-21049: Skip defaultLabel fallback to avoid showing description instead of name
                const multilanguage = params.data?.prcAncillary?.prcMultilanguage;
                if (multilanguage && multilanguage[i18n.language]) {
                    return multilanguage[i18n.language];
                }
                return params.data?.prcAncillary?.nameAncillary;
            }
        },
        {
            field: "relatedDiscountIncreasePercentage",
            headerName: `${t("pricing:ancillaryException.table.labels.discount")}`,
            headerTooltip: `${t("pricing:ancillaryException.table.labels.discount")}`,
            flex: 1,
            filter: "text",
            cellRenderer: (params: any) => {
                return params.data.relatedDiscountIncreasePercentage ?
                    i18nConvertNumberWithDecimals(params.data.relatedDiscountIncreasePercentage) + "%"
                    :
                    params.data.relatedDiscountIncreaseFixed
                        ?
                        i18nConvertNumberWithDecimals(Number(params.data.relatedDiscountIncreaseFixed) / 100)
                        :
                        null;
            }
        },
        {
            field: "reservationFrom",
            headerName: t("pricing:ancillaryException.table.labels.reservationFrom"),
            headerTooltip: t("pricing:ancillaryException.table.labels.reservationFrom"),
            flex: 1,
            valueFormatter: (params: any) => params.data.reservationFrom ? dateFormatter(params.data.reservationFrom) : "",
            filter: "text"
        },
        {
            field: "reservationTo",
            headerName: t("pricing:ancillaryException.table.labels.reservationTo"),
            headerTooltip: t("pricing:ancillaryException.table.labels.reservationTo"),
            flex: 1,
            valueFormatter: (params: any) => params.data.reservationTo ? dateFormatter(params.data.reservationTo) : "",
            filter: "text"
        },
        {
            field: "pickupDateFrom",
            headerName: t("pricing:ancillaryException.table.labels.pickupFrom"),
            headerTooltip: t("pricing:ancillaryException.table.labels.pickupFrom"),
            flex: 1,
            valueFormatter: (params: any) => params.data.pickupFrom ? dateFormatter(params.data.pickupFrom) : "",
            filter: "text"
        },
        {
            field: "pickupDateTo",
            headerName: t("pricing:ancillaryException.table.labels.pickupTo"),
            headerTooltip: t("pricing:ancillaryException.table.labels.pickupTo"),
            flex: 1,
            valueFormatter: (params: any) => params.data.pickupTo ? dateFormatter(params.data.pickupTo) : "",
            filter: "text"
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => {
                console.log(params)
                return (
                    <ActionsPopover
                        url={`../ow/ow-ancillary-exception/create/${params.data.id}`}
                        params={params}
                        view={{
                            action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_VIEW_OW_ANCILLARY_EXCEPTION"
                                })
                            })
                        }}
                        update={{
                            action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_UPDATE_OW_ANCILLARY_EXCEPTION"
                                })
                            })
                        }}
                        terminate={{
                            action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_TERMINATE_OW_ANCILLARY_EXCEPTION"
                                })
                            })
                        }}
                        del={!params.data.used ? {
                            action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                                return profile.auths.find((auth: any) => {
                                    return auth.name === "SCOPE_PRC_DELETE_OW_ANCILLARY_EXCEPTION"
                                })
                            })
                        } : undefined}
                    />
                )
            }
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;
    /* AG GRID - END */

    /* TABLE ACTIONS - START */
    const viewButtonClicked = (data: OWAncillaryExceptionEntityResponse) => {
        setViewOWAncillaryException(true);
        setRowIndex(data.id);
    };

    const updateButtonClicked = (data: OWAncillaryExceptionEntityResponse) => {
        // navigate("../ow/ow-ancillary-exception/create", {
        //     replace: true,
        //     state: {owAncillaryExceptionId: data.id}
        // });
    };

    const terminate = async (id: number) => {
        await terminateOWAncillaryException(id);
        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const terminateButtonClicked = async (data: OWAncillaryEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => terminate(data.id),
            message: t(
                "pricing:ancillaryExceptions.dialogContents.terminateAncillaryExceptionInfo"
            ),
        });
    };

    const deleteButtonClicked = (data: OWAncillaryEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t(
                "pricing:ancillaryExceptions.dialogContents.removeAncillaryExceptionInfo"
            ),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteOWAncillaryException(id);
                setDialog(null);
                await refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };
    /* TABLE ACTIONS - END */

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_OW_ANCILLARY_EXCEPTION";
                        });
                    }) ?
                        {
                            onClick: () => {
                            }
                        } : undefined}
                url={"../ow/ow-ancillary-exception/create/new"}
            />
        )
    };

    return (
        <Box>
            <SectionHeader
                title={t(
                    "translation:auths.SCOPE_PRC_VIEW_OW_ANCILLARY_EXCEPTION.name"
                )}
            />
            <OWAncillaryExceptionFilters
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
                commercialCategoryListOriginal={commercialCategoryListOriginal}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                </Grid>
                {viewOWAncillaryException && rowIndex && (
                    <OWAncillaryExceptionContainerView
                        owAncillaryExceptionId={rowIndex}
                        open={viewOWAncillaryException}
                        setOpen={setViewOWAncillaryException}
                    />
                )}
                {dialog?.open && (
                    <DialogComponent
                        type={"info"}
                        onClickConfirm={dialog.onClickConfirm}
                        setOpen={() => setDialog(null)}
                        open={dialog.open}
                        message={dialog.message}
                    />
                )}
            </Grid>

            <Loading/>
        </Box>
    );
};

export default OWAncillaryExceptions;
